/*
  Micellar Water landing page behaviour.
  Loaded by the LP's topbar section (snippets/mw-lp-assets.liquid), so it follows
  the sections onto any template whatever that template is named.

  Adding to cart reuses the theme's own mechanism: fetchConfig() from global.js,
  routes.cart_add_url, and <cart-drawer>.renderContents() — which re-renders the
  drawer sections and opens it. Nothing here re-implements the cart.
*/

(() => {
  const debounceLocal = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const cartRequestConfig = () => {
    if (typeof fetchConfig === 'function') return fetchConfig('json');
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    };
  };

  const getCart = () =>
    document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  /*
    <mw-cart-button data-items='[{"id":123,"quantity":1}]'>
      <button type="button">…</button>
      <p class="mw-cart-error" data-mw-error></p>
    </mw-cart-button>

    data-discount-code  — after the items land in the cart, hand the cart over to
                          /discount/<code> so the code is applied (used by the
                          routine bundle when the shop has no automatic discount).
  */
  class MwCartButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.errorTarget = this.querySelector('[data-mw-error]');
      if (!this.button || this.bound) return;
      this.bound = true;
      this.button.addEventListener('click', this.onClick.bind(this));
    }

    get items() {
      try {
        return JSON.parse(this.dataset.items || '[]').filter((item) => item && item.id);
      } catch (error) {
        console.error('mw-cart-button: invalid data-items', error);
        return [];
      }
    }

    setLoading(state) {
      this.button.classList.toggle('mw-is-loading', state);
      this.button.toggleAttribute('disabled', state);
    }

    clearError() {
      clearTimeout(this.errorTimer);
      if (this.errorTarget) this.errorTarget.textContent = '';
    }

    showError(message) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = message || (window.cartStrings && window.cartStrings.error) || '';
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => this.clearError(), 6000);
    }

    async onClick(event) {
      event.preventDefault();

      const items = this.items;
      if (!items.length) return;

      const cart = getCart();
      const config = cartRequestConfig();

      this.setLoading(true);
      this.clearError();

      try {
        config.body = JSON.stringify({
          items,
          sections: cart ? cart.getSectionsToRender().map((section) => section.section) : [],
          sections_url: window.location.pathname
        });

        const response = await fetch(window.routes.cart_add_url, config);
        const data = await response.json();

        if (data.status) {
          this.showError(data.description || data.message);
          return;
        }

        const discountCode = this.dataset.discountCode;
        if (discountCode) {
          window.location.href = `/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(
            window.routes.cart_url
          )}`;
          return;
        }

        if (cart) {
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.button);
          cart.renderContents(data);
        } else {
          window.location.href = window.routes.cart_url;
        }

        if (typeof updateCartCounters === 'function') updateCartCounters();
        // The drawer's free-shipping bar is recalculated client-side by the theme (global.js);
        // renderContents() alone leaves it showing the previous cart's remaining amount.
        if (typeof updateFreeShipping === 'function') updateFreeShipping();
      } catch (error) {
        console.error(error);
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get('mw-cart-button')) {
    customElements.define('mw-cart-button', MwCartButton);
  }

  /* Note 3 — "Научи повече" scrolls down to the ingredients module. */
  const initSmoothScroll = () => {
    document.querySelectorAll('[data-mw-scroll]').forEach((link) => {
      if (link.dataset.mwScrollBound) return;
      link.dataset.mwScrollBound = 'true';

      link.addEventListener('click', (event) => {
        const selector = link.getAttribute('href');
        if (!selector || !selector.startsWith('#')) return;

        const target = document.querySelector(selector);
        if (!target) return;

        event.preventDefault();
        const offset = parseInt(link.dataset.mwScrollOffset || '24', 10);
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        const from = window.scrollY;

        window.scrollTo({ top, behavior: 'smooth' });

        // Some engines ignore smooth scrolling; jump rather than go nowhere.
        setTimeout(() => {
          if (window.scrollY === from) window.scrollTo(0, top);
        }, 350);
      });
    });
  };

  /* Hero video: poster first, source attached only once it is about to be seen. */
  const initDeferredVideo = () => {
    const videos = document.querySelectorAll('video[data-mw-src]');
    if (!videos.length) return;

    const load = (video) => {
      if (video.dataset.mwLoaded) return;
      video.dataset.mwLoaded = 'true';
      video.src = video.dataset.mwSrc;
      video.load();
      const play = video.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    };

    if (!('IntersectionObserver' in window)) {
      videos.forEach(load);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          load(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '200px 0px' }
    );

    videos.forEach((video) => observer.observe(video));
  };

  /*
    The theme's setCartLink() binds only the first [data-cart-link] in the document,
    which is the hidden store header's. Wire the topbar's own cart button here, and
    let it fall through to the cart page when there is no drawer.
  */
  const initCartLink = () => {
    document.querySelectorAll('[data-mw-cart-link]').forEach((link) => {
      if (link.dataset.mwCartBound) return;
      link.dataset.mwCartBound = 'true';

      link.addEventListener('click', (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.open !== 'function') return;
        event.preventDefault();
        drawer.open(link);
      });
    });
  };

  /*
    Sticky add-to-cart bar: appears once the hero has scrolled past, retreats again
    when the closing banner comes into view so it never covers the final CTA or the
    footer. Both anchors come from the section's schema.
  */
  /*
    The bottom corners already hold floating widgets — the theme's back-to-top button
    and the tawk.to chat bubble, which paints at z-index 1000003 and on a phone lands
    right on the buy button. They ride up while the bar is out and settle back when it
    leaves, which is what the theme itself does on product pages
    (`.template--product .back-to-top { inset-block-end: 68px }`).

    The chat iframe has a randomly generated id and no class or title, so there is no
    stable selector to write. These are found by geometry instead: anything fixed and
    corner-sized sitting in the band the bar will occupy.
  */
  const WIDGET_LIFT_GAP = 12;
  let liftedWidgets = [];

  const dropWidgets = () => {
    liftedWidgets.forEach(({ el, transform, transition }) => {
      el.style.transform = transform;
      el.style.transition = transition;
    });
    liftedWidgets = [];
  };

  const liftWidgets = (bar) => {
    const bandBottom = window.innerHeight;
    // While hidden the bar is translated below the fold, so its own rect reports no
    // overlap with anything — derive the band from the viewport instead.
    const bandTop = bandBottom - bar.offsetHeight;
    const offset = bar.offsetHeight + WIDGET_LIFT_GAP;

    document.querySelectorAll('body *').forEach((el) => {
      if (el.closest('[data-mw-sticky-bar]')) return;
      // The theme's back-to-top is lifted by CSS instead: it appears on scroll like
      // the bar itself, so a one-off measurement here can race it.
      if (el.closest('back-to-top')) return;
      if (liftedWidgets.some((entry) => entry.el === el)) return;

      const style = getComputedStyle(el);
      if (style.position !== 'fixed' || style.visibility === 'hidden' || style.display === 'none') return;

      const box = el.getBoundingClientRect();
      // Corner widgets only — skip drawers, overlays and full-width bars.
      if (box.width < 20 || box.width > 220 || box.height < 20) return;
      if (box.bottom < bandTop || box.top > bandBottom) return;

      liftedWidgets.push({ el, transform: el.style.transform || '', transition: el.style.transition || '' });
      el.style.setProperty('transition', 'transform 0.28s cubic-bezier(0.2, 0.8, 0.3, 1)', 'important');
      el.style.setProperty('transform', `translateY(-${offset}px)`, 'important');
    });
  };

  const syncWidgets = (bar, visible) => {
    if (visible) {
      liftWidgets(bar);
    } else {
      dropWidgets();
    }
  };

  const initStickyBar = () => {
    const bar = document.querySelector('[data-mw-sticky-bar]');
    if (!bar || bar.dataset.mwStickyBound) return;
    bar.dataset.mwStickyBound = 'true';

    const showAfter = document.querySelector(bar.dataset.showAfter || '.mw-hero');
    const hideAt = document.querySelector(bar.dataset.hideAt || '.mw-final');

    let scrolledPast = false;
    let reachedEnd = false;

    let shown = false;

    const apply = () => {
      const visible = scrolledPast && !reachedEnd;
      bar.classList.toggle('mw-sticky-bar--visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible === shown) return;
      shown = visible;
      document.body.style.setProperty('--mw-sticky-bar-height', bar.offsetHeight + 'px');
      document.body.classList.toggle('mw-sticky-bar-open', visible);
      syncWidgets(bar, visible);
    };

    // Chat widgets load late; pick up anything that appeared after the bar was shown.
    setTimeout(() => {
      if (shown) liftWidgets(bar);
    }, 2500);

    // The lift is measured in pixels, so re-take it when the bar's height changes.
    window.addEventListener(
      'resize',
      debounceLocal(() => {
        if (!shown) return;
        dropWidgets();
        liftWidgets(bar);
      }, 200)
    );

    if (!('IntersectionObserver' in window) || !showAfter) {
      scrolledPast = true;
      apply();
      return;
    }

    new IntersectionObserver(
      ([entry]) => {
        scrolledPast = !entry.isIntersecting;
        apply();
      },
      { threshold: 0 }
    ).observe(showAfter);

    if (hideAt) {
      new IntersectionObserver(
        ([entry]) => {
          reachedEnd = entry.isIntersecting;
          apply();
        },
        { threshold: 0 }
      ).observe(hideAt);
    }

    apply();
  };

  const init = () => {
    initSmoothScroll();
    initDeferredVideo();
    initCartLink();
    initStickyBar();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', init);
})();
